import { useEffect, useState } from "react";
import Filters from "../components/Filters";
import {
  createAdminShow,
  deleteAdminShow,
  fetchAdminShows,
  updateAdminShow,
} from "../api/client";
import ContentFlagSelector from "../components/ContentFlagSelector"; // 🆕 ADD THIS LINE

const emptyFormData = () => ({
  title: "",
  genres: "",
  moods: "",
  platform: "",
  commitment_level: "Movie",
  age_rating: "TV-14",
  laugh_factor: "None",
  cultural_tags: "",
  identity_tags: "",
  representation_tags: "",
  empress_tags: "",
  external_tmdb: "",
  external_imdb: "",
  external_justwatch: "",
  source: "",
  media_type: "",
  content_flags: {}
});

const parseList = (field) =>
  typeof field === "string"
    ? field.split(",").map(f => f.trim()).filter(Boolean)
    : Array.isArray(field)
    ? field.filter(Boolean)
    : [];

const listToField = (value) => Array.isArray(value) ? value.join(", ") : "";

const buildShowPayload = (formData) => ({
  ...formData,
  title: formData.title?.replace(/[<>]/g, "").trim() || "Untitled",
  platform: formData.platform?.trim() || "Unknown",
  age_rating: formData.age_rating || "TV-14",
  laugh_factor: formData.laugh_factor || "None",
  commitment_level: formData.commitment_level || "Movie",
  genres: parseList(formData.genres),
  moods: parseList(formData.moods),
  cultural_tags: parseList(formData.cultural_tags),
  identity_tags: parseList(formData.identity_tags),
  representation_tags: parseList(formData.representation_tags),
  empress_tags: parseList(formData.empress_tags),
  external_ids: {
    tmdb: formData.external_tmdb?.trim() || null,
    imdb: formData.external_imdb?.trim() || null,
    justwatch: formData.external_justwatch?.trim() || null
  },
  source: formData.source?.trim() || null,
  media_type: formData.media_type?.trim() || null,
  content_flags:
    formData.content_flags && !Array.isArray(formData.content_flags)
      ? formData.content_flags
      : {}
});

export default function AdminDashboard() {
  const [shows, setShows] = useState([]);
  const [editingShowId, setEditingShowId] = useState(null);

  // 👇 FORM STATE
  const [formData, setFormData] = useState(emptyFormData);

  // 👇 FILTER STATE (NEW)
  const [filters, setFilters] = useState({
    genre: "",
    mood: "",
    platform: "",
    commitment: ""
  });

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContentFlagChange = (newFlags) => {
    setFormData(prev => ({
      ...prev,
      content_flags: newFlags
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const fetchShows = async () => {
    const res = await fetchAdminShows();
    setShows(res.data);
  };

  const handleAdd = async () => {
    try {
      await createAdminShow(buildShowPayload(formData));
      await fetchShows();
      resetForm();
    } catch (err) {
      console.error("❌ Failed to add show:", err.response?.data || err.message);
    }
  };

  const handleUpdate = async () => {
    if (!editingShowId) return;

    try {
      await updateAdminShow(editingShowId, buildShowPayload(formData));
      await fetchShows();
      resetForm();
      setEditingShowId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (showId) => {
    if (!showId) return;

    await deleteAdminShow(showId);
    await fetchShows();
  };

  const resetForm = () => {
    setFormData(emptyFormData());
  };

  useEffect(() => {
    fetchShows();
  }, []);

  const filteredShows = shows.filter(show => {
    return (
      (!filters.genre || show.genres.includes(filters.genre)) &&
      (!filters.mood || show.moods.includes(filters.mood)) &&
      (!filters.platform || show.platform === filters.platform) &&
      (!filters.commitment || show.commitment_level === filters.commitment)
    );
  });

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-4">Admin Dashboard 👑</h1>

      <Filters onChange={handleFilterChange} shows={shows} />

      <div className="p-4 border rounded shadow mb-6 bg-white mt-6">
        <h3 className="text-lg font-bold mb-2">{editingShowId ? "Edit Show" : "Add New Show"}</h3>
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Title" value={formData.title} onChange={e => handleFormChange("title", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Genres (comma-separated)" value={formData.genres} onChange={e => handleFormChange("genres", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Moods (comma-separated)" value={formData.moods} onChange={e => handleFormChange("moods", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Platform" value={formData.platform} onChange={e => handleFormChange("platform", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Age Rating (e.g. TV-14)" value={formData.age_rating} onChange={e => handleFormChange("age_rating", e.target.value)} />
        <select className="w-full mb-2 p-2 border" value={formData.laugh_factor} onChange={e => handleFormChange("laugh_factor", e.target.value)}>
          <option value="Laugh Factor Placeholder">Laugh Factor</option>
          <option value="None">None</option>
          <option value="Dry">Dry</option>
          <option value="Witty">Witty</option>
          <option value="Subtle">Subtle</option>
        </select>
        <select className="w-full mb-2 p-2 border" value={formData.commitment_level} onChange={e => handleFormChange("commitment_level", e.target.value)}>
          <option value="Commitment Level Placeholder">Commitment Level</option>
          <option value="Movie">Movie</option>
          <option value="Limited Series">Limited Series</option>
          <option value="Multi-Season">Multi-Season</option>
        </select>
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Cultural Tags (comma-separated)" value={formData.cultural_tags} onChange={e => handleFormChange("cultural_tags", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Identity Tags (comma-separated)" value={formData.identity_tags} onChange={e => handleFormChange("identity_tags", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Representation Tags (comma-separated)" value={formData.representation_tags} onChange={e => handleFormChange("representation_tags", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Empress Tags (comma-separated)" value={formData.empress_tags} onChange={e => handleFormChange("empress_tags", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="TMDb ID" value={formData.external_tmdb} onChange={e => handleFormChange("external_tmdb", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="IMDb ID" value={formData.external_imdb} onChange={e => handleFormChange("external_imdb", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="JustWatch ID" value={formData.external_justwatch} onChange={e => handleFormChange("external_justwatch", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Source" value={formData.source} onChange={e => handleFormChange("source", e.target.value)} />
        <input className="w-full mb-2 p-2 border" type="text" placeholder="Media Type" value={formData.media_type} onChange={e => handleFormChange("media_type", e.target.value)} />

        {/* 🧿 New Tag Picker for Content Flags */}
        <div className="mb-4">
          <ContentFlagSelector selectedFlags={formData.content_flags} onChange={handleContentFlagChange} />
        </div>

        <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded" onClick={editingShowId ? handleUpdate : handleAdd}>
          {editingShowId ? "Save Changes" : "Add Show"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredShows.map((show) => (
          <div key={show.title} className="p-4 rounded border shadow bg-white">
            <h2 className="text-xl font-bold text-purple-800 mb-1">{show.title}</h2>
            <p className="text-sm italic text-gray-500 mb-1">Streaming on: {show.platform}</p>
            <p><strong>🍿 Genres:</strong> {show.genres.join(", ")}</p>
            <p><strong>💞 Moods:</strong> {show.moods.join(", ")}</p>
            <p><strong>Identity:</strong> {(show.identity_tags || []).join(", ")}</p>
            <p><strong>Representation:</strong> {(show.representation_tags || []).join(", ")}</p>
            <p><strong>Empress:</strong> {(show.empress_tags || []).join(", ")}</p>
            <p><strong>🤣 Laugh Factor:</strong> {show.laugh_factor}</p>
            <p><strong>⏱️ Commitment:</strong> {show.commitment_level}</p>
            <div className="flex mt-2 gap-2">
              <button
                className="text-blue-600 font-bold"
                onClick={() => {
                  setEditingShowId(show.id);
                  setFormData({
                    title: show.title,
                    genres: listToField(show.genres),
                    moods: listToField(show.moods),
                    platform: show.platform,
                    commitment_level: show.commitment_level,
                    age_rating: show.age_rating,
                    laugh_factor: show.laugh_factor,
                    cultural_tags: listToField(show.cultural_tags),
                    identity_tags: listToField(show.identity_tags),
                    representation_tags: listToField(show.representation_tags),
                    empress_tags: listToField(show.empress_tags),
                    external_tmdb: show.external_ids?.tmdb || "",
                    external_imdb: show.external_ids?.imdb || "",
                    external_justwatch: show.external_ids?.justwatch || "",
                    source: show.source || "",
                    media_type: show.media_type || "",
                    content_flags: show.content_flags || {}
                  });
                }}
              >
                Edit
              </button>
              <button className="text-red-600 font-bold" onClick={() => handleDelete(show.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
